/**
 * Read models — what the endpoints actually return.
 *
 * This file exists because the domain types in `master.ts`, `fraud.ts` and
 * friends describe the *domain*, and several read endpoints do not return the
 * domain shape. `Outlet` nests a `location`; `GET /masterdata/outlets` returns
 * flat `lat`/`lng`. `FlaggedVisit` carries `dispositionByUserId` and its signals
 * carry `evaluatedAt`; `GET /fraud/flags` returns neither, and adds `flagId` to
 * each signal instead. Typing a client with the domain type would compile
 * cleanly and read `undefined` at runtime, which is the worst of both.
 *
 * So: these are declared as TypeScript types rather than zod schemas. That is a
 * deliberate limit. A zod schema here would look like a validator and invite
 * someone to parse responses with it, which would turn any additive server
 * change into a client-side crash. The API's own repositories are the authority;
 * these types exist so the console is not written against `any`.
 *
 * ONE TRAP ENCODED BELOW, which type-checks silently if you get it wrong:
 * drizzle's `avg()` and `sum()` return NUMERIC, which the pg driver hands back
 * as a STRING to avoid precision loss. Every `average*`, `min*` and `max*` field
 * here is `string | null`, not `number | null`. Rendering one without
 * `Number(...)` prints "87.33333333333333".
 *
 * Every list endpoint returns `Page<T>`, never a bare array. See the `Page`
 * doc comment for why that is worth the extra `.data` at every call site.
 */
/** Applied when a request omits `pageSize`. */
export const DEFAULT_PAGE_SIZE = 25;
/**
 * The ceiling on `pageSize`. A larger value is clamped, not rejected — a caller
 * asking for more rows than the server will serve is not making an error, and a
 * 400 there would turn a tuning choice into an outage.
 */
export const MAX_PAGE_SIZE = 200;
/**
 * The page size the reference lists default to.
 *
 * Regions, competitors and channels back `<select>` pickers, where paging is
 * meaningless — a picker that shows the first 25 of 60 regions is broken, not
 * paginated. These lists are bounded by business reality (a country has tens of
 * regions), not by data growth, so they return one large page and their hooks
 * read `.data` directly. This is the deliberate exception, written down rather
 * than implied by a magic number at five call sites.
 */
export const REFERENCE_PAGE_SIZE = MAX_PAGE_SIZE;
/**
 * `to - from` may not exceed this, or the API answers 400.
 *
 * Counted in WHOLE DAYS. A window is a pair of dates widened to cover both of
 * them — `from` at midnight, `to` at `23:59:59.999` — so the trailing part-day is
 * not charged against the cap. Measuring the raw span instead makes the 90-day
 * range the console itself produces 91 milliseconds-worth of days over the line.
 */
export const REPORT_WINDOW_MAX_DAYS = 90;
/**
 * The row cap on the export path.
 *
 * `POST /reporting/export` is the one read that is deliberately NOT paginated —
 * an export of page 1 is not an export. It keeps a hard cap instead, because an
 * unbounded row-level query over a 90-day window is the one that starves the
 * connection pool.
 */
export const EXPORT_ROW_CAP = 50_000;
/**
 * The three caps on the photo export, and why it needs three rather than one.
 *
 * `POST /reporting/export/photos` packs the ORIGINAL PHOTOGRAPHS behind a
 * filtered visit report into one ZIP. It is the only export whose size is not
 * predictable from its row count: a 90-day window can legitimately name tens of
 * thousands of visits, each carrying up to seven images of a few hundred
 * kilobytes, and the archive is assembled in memory in the api process. The
 * workbook exporter's 50,000 rows produce a file of a few megabytes; the same
 * window here would produce hundreds of gigabytes.
 *
 * SO THE REQUEST IS REFUSED RATHER THAN TRUNCATED. A silently shortened archive
 * is the worse failure: the manager receives a plausible ZIP, and nothing in it
 * says which visits were dropped. A 400 naming the cap sends them back to the
 * filter, which is the control that actually answers the question they asked.
 *
 * THREE CAPS BECAUSE THERE ARE THREE WAYS TO BE TOO BIG, and they do not imply
 * one another. Many visits with one photo each is a folder problem; few visits
 * with seven high-resolution photos each is a memory problem; and the byte
 * budget is the only one of the three that bounds heap. All three are checked
 * against `compressed_bytes` and the row set BEFORE a single object is fetched
 * from storage, so an over-budget request costs one indexed query.
 */
export const PHOTO_EXPORT_MAX_VISITS = 500;
export const PHOTO_EXPORT_MAX_PHOTOS = 2_000;
/** 1 GiB. The binding cap in practice — see the note above. */
export const PHOTO_EXPORT_MAX_BYTES = 1_073_741_824;
/*
 * THE COMPLETE EXPORTS SHARE THE PHOTO AND BYTE CAPS, AND ONLY THOSE. The
 * download centre's jobs (`exports.ts`) pack the same photographs the photo
 * export does, fetched the same way, so the two caps that bound heap bound them
 * too. The VISIT cap is the photo export's alone: that export runs inside an
 * api request, and 500 is what keeps a request-path export short. A job runs
 * on the worker, one at a time, with nobody waiting on a connection — so each
 * kind carries a folder cap of its own, below.
 */
/**
 * The folder cap on the Laporan Kunjungan complete export — `VISITS_DETAIL`.
 *
 * TEN TIMES THE PHOTO EXPORT'S, because the constraint is different in kind.
 * A month of one team's visits is the question this export is opened for, and
 * on a team of twenty that is well past 500 visits; refusing it on the visit
 * count alone sent managers back to the filter for an archive the two real
 * caps would have allowed. What 5,000 still bounds is the archive as a thing a
 * person opens — a workbook per folder — and the ZIP format's own ceiling of
 * 65,535 entries. A window whose PHOTOGRAPHS are too many or too large is still
 * refused by the two caps above, which are what protect the worker's memory.
 */
export const DETAIL_EXPORT_MAX_VISITS = 5_000;
/**
 * The folder cap on the Indeks Harga complete export — `PRICE_PAIRS_DETAIL`.
 *
 * That job packs one folder per row of the paired report, and a row there is a
 * (SKU, plan) pair rather than a visit — the whole internal catalogue at most,
 * which is hundreds, not thousands. Five hundred is where an archive of pairs
 * stops being something a manager opens and starts being something they
 * search. The photo and byte caps above apply unchanged, counted over the
 * DISTINCT photographs the archive holds: each is stored once, in `Foto/`,
 * however many pairs it is evidence for.
 */
export const PRICE_PAIRS_EXPORT_MAX_PAIRS = 500;
/**
 * The folder cap on the Indeks Harga Detail complete export — `PRICE_VISITS_DETAIL`.
 *
 * A row there is one store visit on one competition plan, so a month of one
 * team's visits is a few thousand rows by the same arithmetic that sets
 * `DETAIL_EXPORT_MAX_VISITS`, and the same number bounds it for the same
 * reason: folders a person opens, and the ZIP format's 65,535 entries. The
 * photo and byte caps are what protect the worker, counted over distinct
 * photographs — a visit's shelf photo serves every plan that visit priced and
 * is stored once.
 */
export const PRICE_VISITS_EXPORT_MAX_ROWS = 5_000;
/**
 * The folder cap on the Kepatuhan Planogram complete export — `PLANOGRAM_DETAIL`.
 *
 * A row there is one internal SKU — the catalogue, benchmarked or not, so the
 * row set is hundreds at most and 500 is the same number the paired price
 * report uses for the same reason: folders a person opens. The photo and byte
 * caps apply over distinct photographs, stored once in `Foto/`; a shelf
 * photograph carrying nine of our products is evidence for nine rows and one
 * file.
 */
export const PLANOGRAM_EXPORT_MAX_SKUS = 500;
/**
 * The timezone month boundaries are computed in.
 *
 * This is the first timezone constant in the codebase and it is load-bearing: it
 * decides which month a 23:00-WIB visit belongs to, and therefore which monthly
 * target it is measured against.
 */
export const REPORT_TIME_ZONE = 'Asia/Jakarta';
/**
 * How far back `PriceVisitRow.repeatCount` looks.
 *
 * NINETY DAYS, matching `REPORT_WINDOW_MAX_DAYS` above and
 * `PRICE_CARRY_FORWARD_MAX_AGE_DAYS` in the api — not a coincidence and not
 * cargo-culted. All three are answering the same question about the same field
 * operation: how far back is a price reading still worth anything. A shorter
 * window would report "1×" on nearly every row, because at a monthly visit cycle
 * one shop prices one product about once a month.
 */
export const PRICE_REPEAT_WINDOW_DAYS = 90;
/**
 * When `repeatCount` stops being a fact and starts being a finding.
 *
 * ONE — every off-plan visit carries the mark from its first reading. This
 * shipped as two, on the argument that one observation is a fact and only the
 * second ("raised it, still unchanged") is a story; the product owner tested it
 * on a fresh outlet, found a "Perlu penyesuaian" row with no mark beside it, and
 * asked why. The mark, in their reading, is a MONTH COUNTER on the verdict —
 * bulan ke-1, ke-2, ke-3 — and a counter that is blank for the whole of month
 * one is a counter that has to be explained. The first/repeat distinction still
 * exists; it moved into the tooltip's wording, where it can be said in a
 * sentence rather than by absence.
 *
 * In the contract rather than in the console so the screen's highlight and the
 * export's wording cannot drift about which rows are the alert.
 */
export const PRICE_REPEAT_ALERT_MIN = 1;
/**
 * The entities `POST /masterdata/import/:entity` accepts.
 *
 * `USER` is deliberately absent even though the server parses it: the importer
 * answers HTTP 200 with `imported: 0` and "user import is not supported here" in
 * `errors`, so offering it in a UI would be offering a no-op. `PLANOGRAM` left
 * this list for the same reason once the planogram master was dropped — the
 * shelf plan arrives as columns on the SKU import now. The value survives in
 * `ImportResult['entity']` because `import_batches` history still carries it.
 *
 * ORDERED BY DEPENDENCY, not alphabetically, because the list is rendered as the
 * entity picker and the order is the running order: an outlet row names a region
 * that has to exist first, and a SKU row names a competitor and a category. A
 * picker that opens on `SKU` when the install has no regions yet sends an
 * operator down the wrong path first.
 */
export const IMPORTABLE_ENTITIES = [
    'REGION',
    'SKU',
    'OUTLET',
    'COMPETITOR',
    // Last because it depends on two of the others plus the user list: a target
    // names a salesman and the region they are planned in, and both have to exist.
    'VISIT_TARGET',
    // After SKU for the same reason: a plan names two catalogue entries, one of
    // each type, and both have to exist before the pair can be stated.
    'COMPETITION_PLAN',
];
/**
 * Permissions a bulk import or export needs ON TOP OF `masterdata:import`.
 *
 * WHY THIS MAP EXISTS RATHER THAN ONE PERMISSION FOR EVERYTHING. `target:write`
 * is ADMIN-only on purpose — the seed says so at length: a visit target is the
 * denominator of every attainment figure on the dashboard, and letting the
 * person measured by it edit it is a decision somebody has to make deliberately.
 * If `POST /masterdata/import/VISIT_TARGET` were guarded by `masterdata:import`
 * alone, granting bulk import to a regional manager — an entirely reasonable
 * thing for an administrator to do through the role matrix — would silently hand
 * them the quota-setting power that policy withholds. That is exactly the drift
 * ADR-0006 records, arriving through a side door.
 *
 * So the rule is: bulk access never grants more than the single-row endpoints
 * for the same entity do. The four originals need nothing extra, because
 * `masterdata:import` already implies replacing those tables wholesale.
 *
 * READ BY BOTH SIDES. The controller enforces it and the console filters its
 * entity picker with it, so a button is never offered that the server refuses.
 */
export const IMPORT_EXTRA_PERMISSIONS = {
    VISIT_TARGET: 'target:write',
    // A competition plan is a target too — its controller says so at length, and
    // it is guarded by `target:*` rather than `sku:*` for exactly that reason.
    COMPETITION_PLAN: 'target:write',
};
/** The read half of the same rule, for `POST /masterdata/export/:entity`. */
export const EXPORT_EXTRA_PERMISSIONS = {
    VISIT_TARGET: 'target:read',
    COMPETITION_PLAN: 'target:read',
};
/** The multipart form field name the importer reads. Exactly this. */
export const IMPORT_FILE_FIELD = 'file';
/** Server-side cap on the uploaded file, in bytes. */
export const IMPORT_MAX_BYTES = 25 * 1024 * 1024;
/**
 * The entities `POST /masterdata/export/:entity` can produce a file for.
 *
 * THE SAME SET AS `IMPORTABLE_ENTITIES`, which is where it was always heading —
 * it started as a subset while the per-CRUD buttons rolled out, and closed once
 * COMPETITOR landed. Keep them equal: the export exists to feed the importer — its columns are the header aliases
 * `import.service.ts` reads, so the file it produces is one an operator edits in
 * Excel and uploads straight back. An entity that could be exported but not
 * imported would be a file with nowhere to go.
 *
 * Still its own literal union rather than an alias of the import list, now that
 * the two agree. An alias would make a future entity exportable the moment it
 * became importable, silently and without a case in `export.service.ts` — the
 * separate union is what makes forgetting the other half a compile error rather
 * than a 400 discovered by an operator.
 *
 * NO FORMAT PARAM ANYWHERE. The three report exporters each carry their own
 * `xlsx | pdf` union because a report has a printable half; a round-trip file
 * does not — a PDF of a master table cannot be uploaded back, so offering the
 * choice would be offering a dead end.
 */
export const EXPORTABLE_ENTITIES = [
    'REGION',
    'SKU',
    'OUTLET',
    'COMPETITOR',
    'VISIT_TARGET',
    'COMPETITION_PLAN',
];
/** Turns the flat pair list into the matrix every UI actually wants. */
export function groupPermissions(pairs) {
    const matrix = new Map();
    for (const pair of pairs) {
        const existing = matrix.get(pair.roleId);
        if (existing)
            existing.add(pair.permission);
        else
            matrix.set(pair.roleId, new Set([pair.permission]));
    }
    return matrix;
}
//# sourceMappingURL=wire.js.map